import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Bell, BellOff, CheckCheck, ClipboardList, Crown, AlarmClock, Handshake } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";

const ICONS = { novo_pedido: ClipboardList, pedido_aceito: Handshake, pro_ativo: Crown, pro_vencendo: AlarmClock };
const fmt = (d) => new Date(d).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });

function Item({ n, onOpen }) {
  const Icon = ICONS[n.kind] || Bell;
  return (
    <button type="button" onClick={() => onOpen(n)} data-testid={`notification-${n.id}`} data-read={n.read ? "true" : "false"}
      className={`flex w-full items-start gap-4 rounded-3xl border-2 p-4 text-left transition-colors ${n.read ? "border-slate-200 bg-white" : "border-orange-400 bg-orange-50 hover:bg-orange-100"}`}>
      <span className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl ${n.read ? "bg-slate-100 text-slate-500" : "bg-orange-500 text-white"}`}>
        <Icon className="h-6 w-6" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-2">
          <span className={`text-lg ${n.read ? "font-semibold text-slate-700" : "font-extrabold text-slate-900"}`}>{n.title}</span>
          {!n.read && <span className="h-3 w-3 shrink-0 rounded-full bg-red-600" data-testid={`notification-unread-dot-${n.id}`} />}
        </span>
        <span className="block text-base text-slate-700">{n.body}</span>
        <span className="mt-1 block text-sm text-slate-500">{fmt(n.created_at)}</span>
      </span>
    </button>
  );
}

export default function Notifications() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({ queryKey: ["notifications"], queryFn: () => api.get("/notificacoes").then((r) => r.data), refetchInterval: 20000 });
  const refresh = () => { qc.invalidateQueries({ queryKey: ["notifications"] }); qc.invalidateQueries({ queryKey: ["notif-count"] }); };
  const open = async (n) => {
    if (!n.read) { try { await api.post(`/notificacoes/${n.id}/lida`); } catch (e) { /* navigation still works */ } refresh(); }
    if (n.url) navigate(n.url);
  };
  const readAll = async () => {
    try { await api.post("/notificacoes/todas-lidas"); toast.success("Todas marcadas como lidas"); refresh(); } catch (e) { toast.error(errMsg(e)); }
  };
  const items = data?.items || [];
  return (
    <div className="mx-auto max-w-2xl px-4 py-8 sm:px-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="flex items-center gap-3 font-display text-3xl font-extrabold text-slate-900 sm:text-4xl">
          <Bell className="h-8 w-8 text-orange-500" /> Notificações
        </h1>
        {data?.unread > 0 && (
          <button onClick={readAll} className="po-btn-primary min-h-[52px] px-5 text-base" data-testid="notifications-read-all">
            <CheckCheck className="h-5 w-5" /> Marcar todas como lidas
          </button>
        )}
      </div>
      {data && <p className="mt-2 text-lg text-slate-600" data-testid="notifications-unread-count">{data.unread ? `${data.unread} não lida(s)` : "Tudo lido"}</p>}
      <div className="mt-5 space-y-3" data-testid="notifications-list">
        {items.map((n) => <Item key={n.id} n={n} onOpen={open} />)}
        {!isLoading && items.length === 0 && (
          <div className="po-card flex flex-col items-center gap-3 py-10 text-center" data-testid="notifications-empty">
            <BellOff className="h-12 w-12 text-slate-400" />
            <p className="text-xl font-bold text-slate-700">Você não tem notificações</p>
          </div>
        )}
      </div>
    </div>
  );
}
